import * as React from 'react';
import { Step, StepLabel, Stepper } from '@mui/material';
import { useState } from 'react';
import { KeyDate } from 'routes/Home/models/KeyDate';
import EditIcon from '@mui/icons-material/Edit';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import {
    useGetBudgetKeyDates,
    useUpdateKeyDate,
} from 'routes/Home/hooks/useGetBudgetKeyDates';
import Flex from 'ui/Flex/Flex';
import TooltipIconButton from 'components/TooltipIconButton';
import { useTranslation } from 'react-i18next';
import { useIsSuperFinancesWriter } from 'hooks/user';
import { getActiveStep } from './helpers';
import BudgetKeyDateLabel from './BudgetKeyDateLabel';
import HighlightBox from 'components/SharedComponents/HighlightContainer/HighlightBox';
import { BUDGET_KEY_DATES_EVENT } from 'routes/Home/components/BudgetKeyDates/constants';
import { useQueryClient } from '@tanstack/react-query';

const BudgetKeyDates = ({
    onConfirm = undefined,
}: {
    onConfirm?: () => void;
}): JSX.Element => {
    const [isEditing, setIsEditing] = useState(false);
    const [errors, setErrors] = useState<Record<string, string | undefined>>(
        {},
    );
    const queryClient = useQueryClient();
    const isSuperFinancesWriter = useIsSuperFinancesWriter();
    const { data: keyDatesByExercice = {} } = useGetBudgetKeyDates();
    const { mutate: updateKeyDate } = useUpdateKeyDate();
    const { t } = useTranslation();

    const getKeyDateFromEvent = (
        event: string,
        exercice: string,
    ): KeyDate | undefined =>
        keyDatesByExercice?.[exercice].find((kd) => kd.event === event);

    const setErrorForEvent = (event: string, error: string | undefined) => {
        setErrors((prevState) => {
            const newState = prevState;
            newState[event] = error;
            return { ...newState };
        });
    };

    const editKeyDates = (): void => {
        setIsEditing(true);
    };

    const onDateChange = (
        date: string,
        event: string,
        exercice: string,
    ): void => {
        const index = BUDGET_KEY_DATES_EVENT.findIndex((e) => e === event);

        // Check if the date is not before the previous date set.
        if (index > 0) {
            const previousKeyDate = getKeyDateFromEvent(
                BUDGET_KEY_DATES_EVENT[index - 1],
                exercice,
            );
            if (
                previousKeyDate?.scheduledAt &&
                new Date(date) < new Date(previousKeyDate.scheduledAt)
            ) {
                setErrorForEvent(event, t('home.keyDates.invalidDate'));
                return;
            }
        }

        // Check if the next date is not before the current date set.
        const nextKeyDate = getKeyDateFromEvent(
            BUDGET_KEY_DATES_EVENT[index + 1],
            exercice,
        );
        if (nextKeyDate) {
            if (
                nextKeyDate?.scheduledAt &&
                new Date(date) > new Date(nextKeyDate.scheduledAt)
            ) {
                setErrorForEvent(
                    nextKeyDate.event,
                    t('home.keyDates.invalidDate'),
                );
            } else {
                setErrorForEvent(nextKeyDate.event, undefined);
            }
        }

        // Reset error for the current event.
        setErrorForEvent(event, undefined);

        // Edit the key dates.
        queryClient.setQueryData(
            ['budget_key_dates'],
            (keyDates: KeyDate[]) => {
                return keyDates.map((keyDate) => {
                    if (
                        keyDate.event === event &&
                        keyDate.exercice === parseInt(exercice)
                    ) {
                        return {
                            ...keyDate,
                            scheduledAt: date,
                            edited: true,
                        };
                    }
                    return keyDate;
                });
            },
        );
    };

    const saveKeyDateChanges = (): void => {
        const keyDates = Object.values(keyDatesByExercice).flat();
        keyDates.forEach((keyDate) => {
            if (keyDate.edited) {
                updateKeyDate({
                    event: keyDate.event,
                    scheduledAt: keyDate.scheduledAt,
                    exercice: keyDate.exercice,
                });
            }
        });
        setIsEditing(false);
        if (onConfirm) {
            onConfirm();
        }
    };

    // The Validation button is disabled when one of the date picker is in an error state
    const isDisabled = Object.values(errors).some((error) => error);
    const keyDatesExercices = Object.keys(keyDatesByExercice).sort(
        (a, b) => parseInt(b) - parseInt(a),
    );
    return (
        <HighlightBox
            sx={{ height: 'fit-content', flexDirection: 'column', gap: 2 }}
        >
            {keyDatesExercices.map((exercice) => {
                const keyDates = keyDatesByExercice[exercice];
                return (
                    <Flex key={exercice} direction="column" sx={{ gap: 2 }}>
                        <Flex
                            direction="row"
                            align="center"
                            justify="space-between"
                        >
                            <Flex align="center" sx={{ gap: 2 }}>
                                <AccountBalanceIcon color="primary" />
                                <Typography variant="h5">
                                    {t('home.keyDates.title')} {exercice}
                                </Typography>
                            </Flex>
                            {isSuperFinancesWriter && (
                                <Flex align="center" justify="end">
                                    {isEditing ? (
                                        <TooltipIconButton
                                            onClick={saveKeyDateChanges}
                                            icon={<CheckIcon />}
                                            isDisabled={isDisabled}
                                            tooltip={t('common.validate')}
                                        />
                                    ) : (
                                        <TooltipIconButton
                                            onClick={editKeyDates}
                                            icon={<EditIcon />}
                                            tooltip={t('common.edit')}
                                        />
                                    )}
                                </Flex>
                            )}
                        </Flex>
                        <Stepper
                            activeStep={getActiveStep(
                                keyDates,
                                BUDGET_KEY_DATES_EVENT,
                            )}
                            alternativeLabel
                            sx={{ width: '100%' }}
                        >
                            {BUDGET_KEY_DATES_EVENT.map((event) => (
                                <Step key={event}>
                                    <StepLabel>
                                        <BudgetKeyDateLabel
                                            isEditing={isEditing}
                                            keyDate={getKeyDateFromEvent(
                                                event,
                                                exercice,
                                            )}
                                            onDateChange={onDateChange}
                                            error={errors[event]}
                                            exercice={exercice}
                                        />
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Flex>
                );
            })}
        </HighlightBox>
    );
};

export default BudgetKeyDates;
